<template>
  <modal-wrap :id="id" size="xl" :name="modalName" :hide-footer="true">
    <template #modal-title>
      <h5>{{ modalTitle }}</h5>
    </template>
    <template #default>
      <VeeForm @submit="submit">
        <div class="mt-2">
          <div class="d-flex mr-4">
            <div class="form-group col-xl-6 mr-4">
              <Field
                v-slot="{ value, meta }"
                :model-value="formData.carrierHandle"
                name="carrierHandle"
                :rules="{ required: true }"
              >
                <label for="company">Carrier *</label>
                <div class="input-group">
                  <select
                    v-model="formData.carrierHandle"
                    :class="['form-control', getValidationState(meta)]"
                    name="carrierHandle"
                  >
                    <option
                      v-for="(carrier, key) in carriers"
                      :key="key"
                      :value="carrier.value"
                      :selected="value === carrier.value"
                    >
                      {{ carrier.text }}
                    </option>
                  </select>
                </div>
              </Field>
            </div>
            <div class="form-group col-xl-6">
              <Field v-slot="{ value, meta }" :model-value="formData.mode" name="mode" :rules="{ required: true }">
                <label for="company">Mode *</label>
                <div class="input-group">
                  <select v-model="formData.mode" :class="['form-control', getValidationState(meta)]" name="mode">
                    <option
                      v-for="(mode, key) in modes"
                      :key="key"
                      :value="mode.value"
                      :selected="value === mode.value"
                    >
                      {{ mode.text }}
                    </option>
                  </select>
                </div>
              </Field>
            </div>
          </div>

          <div class="d-flex mr-4">
            <div class="form-group col-xl-6 mr-4">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="formData.minPackagesAmount"
                name="minPackagesAmount"
                :rules="{ required: true, numeric: true }"
              >
                <label for="company">Min Packages Amount *</label>
                <input
                  v-bind="field"
                  v-model="formData.minPackagesAmount"
                  type="number"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>

            <div v-if="formData.mode === TIER" class="form-group col-xl-6">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="formData.minPrice"
                name="minPrice"
                :rules="{ required: true, numeric: true }"
              >
                <label for="company">Min Price *</label>
                <input
                  v-bind="field"
                  v-model="formData.minPrice"
                  type="number"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>

          <div v-if="formData.mode === TIER" class="d-flex mr-4">
            <div class="form-group col-xl-6 mr-4">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="formData.pricePerPiece"
                name="pricePerPiece"
                :rules="{ required: true, numeric: true }"
              >
                <label for="company">Price Per Piece *</label>
                <input
                  v-bind="field"
                  v-model="formData.pricePerPiece"
                  type="number"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
            <div class="form-group col-xl-6">
              <Field
                v-slot="{ field, errors, meta }"
                :model-value="formData.pricePerKg"
                name="pricePerKg"
                :rules="{ required: true, numeric: true }"
              >
                <label for="company">Price Per Kg *</label>
                <input
                  v-bind="field"
                  v-model="formData.pricePerKg"
                  type="number"
                  :class="['form-control', getValidationState(meta)]"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>

          <div class="d-flex mr-4">
            <div class="form-group col-xl-6 mr-4">
              <Field v-slot="{ value, meta }" :model-value="formData.countryGroup" name="countryGroup">
                <label for="company">Country Group</label>
                <div class="input-group">
                  <select
                    v-model="formData.countryGroup"
                    :class="['form-control', getValidationState(meta)]"
                    name="countryGroup"
                  >
                    <option
                      v-for="(countryGroup, key) in countryGroups"
                      :key="key"
                      :value="countryGroup.value"
                      :selected="value === countryGroup.value"
                    >
                      {{ countryGroup.text }}
                    </option>
                  </select>
                </div>
              </Field>
            </div>
            <div v-if="!formData.countryGroup" class="form-group col-xl-6">
              <Field v-slot="{ field, errors, meta }" :model-value="formData.countryCode" name="countryCode">
                <label for="country">Country *</label>
                <CountryPicker
                  v-bind="field"
                  :class="['form-control', getValidationState(meta)]"
                  @change="selectCountryCode"
                />
                <span class="text-danger">{{ errors.join() }}</span>
              </Field>
            </div>
          </div>
        </div>

        <div v-if="formData.mode === WEIGHT">
          <div class="modal-table">
            <div class="modal-table__title">Weight prices</div>
          </div>
          <div class="table-shadow bg-dark-green-title table-editable mt-2">
            <table class="table bg-table-cell-dark">
              <thead>
                <tr>
                  <th>Weight (min)</th>
                  <th>Weight (max)</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(weight, index) in formData.weights" :key="index">
                  <td class="p-0">
                    <div class="form-group form-group_bg_white">
                      <input v-model="weight.minWeight" type="text" class="form-control form-control_hide_arrow" />
                    </div>
                  </td>
                  <td class="p-0">
                    <div class="form-group form-group_bg_white">
                      <input v-model="weight.maxWeight" type="text" class="form-control form-control_hide_arrow" />
                    </div>
                  </td>
                  <td class="p-0">
                    <div class="form-group form-group_bg_white">
                      <input v-model="weight.price" type="text" class="form-control form-control_hide_arrow" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <span class="add-piece-cta mr-auto" @click="addWeight">
            <img src="@/assets/img/add-client-icon.svg" alt="" class="mr-2" />
            Add weight
          </span>
        </div>

        <div class="raw text-right">
          <b-button size="md" class="btn-grey mr-3" @click="closeModal"> Cancel </b-button>
          <b-button size="md" class="btn-green" type="submit"> Save </b-button>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { PriceFormData } from '@/types/Components/FormData'
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { BasePrice, BasePriceWeight } from '@/types/Models/BasePrice'
import { BButton } from 'bootstrap-vue-next'
import { Field, Form as VeeForm } from 'vee-validate'
import { computed, defineComponent, type ComputedRef, ref, type Ref, watch, onMounted } from 'vue'
import { DOMESTIC, INTERNATIONAL } from '@/const/AssignmentDestinationTypes'
import { PRIORITY } from '@/const/AssignmentServiceLevels'
import { CARRIERS_NAMES, POSTNORD } from '@/const/Carriers'
import { EU, NON_EU } from '@/const/CountryGroups'
import { EDIT_PRICE_MODAL } from '@/const/ModalTypes'
import { TIER, WEIGHT } from '@/const/PriceModes'
import { BASE } from '@/const/PriceTypes'
import { getValidationState } from '@/services/Validation'
import { usePricesStore } from '@/store/prices'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'

export default defineComponent({
  name: 'EditPriceModal',
  components: { BButton, CountryPicker, Field, ModalWrap, VeeForm },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const pricesStore = usePricesStore()
    const modalStore = useVueNextModalStore()

    const modalName = EDIT_PRICE_MODAL
    const defaultPriceData: PriceFormData = {
      id: null,
      type: BASE,
      mode: TIER,
      carrierHandle: POSTNORD,
      destination: INTERNATIONAL,
      countryCode: null,
      countryGroup: null,
      serviceLevel: PRIORITY,
      minPackagesAmount: 1,
      pricePerKg: 0,
      pricePerPiece: 0,
      minPrice: 0,
      weights: [],
    }
    const defaultWeightData: BasePriceWeight = {
      minWeight: 0,
      maxWeight: 1000,
      price: 0,
    }
    const carriers: SelectOptions[] = Object.keys(CARRIERS_NAMES).map((key: string) => ({
      text: CARRIERS_NAMES[key],
      value: key,
    }))
    const modes: SelectOptions[] = [
      { text: 'Tier', value: TIER },
      { text: 'Weight', value: WEIGHT },
    ]
    const countryGroups: SelectOptions[] = [
      { text: 'By Country', value: null },
      { text: 'European Union', value: EU },
      { text: 'Other Countries', value: NON_EU },
    ]

    const formData: Ref<PriceFormData> = ref({ ...defaultPriceData })

    const modalData: ComputedRef<BasePrice | null> = computed(() => modalStore.modalPropsByName(modalName) || null)
    const modalTitle: ComputedRef<string> = computed(() => (modalData.value?.id ? 'Edit Price' : 'Add Price'))

    const closeModal = () => {
      modalStore.closeModal(modalName)
    }
    const selectCountryCode = (value: string) => {
      formData.value.countryCode = value
    }
    const addWeight = () => {
      let weight = { ...defaultWeightData } as BasePriceWeight
      if (formData.value.weights.length) {
        const previousWeight = formData.value.weights[formData.value.weights.length - 1] || defaultWeightData
        weight.minWeight = parseInt(String(previousWeight.maxWeight))
        weight.maxWeight = weight.minWeight + parseInt(String(previousWeight.maxWeight - previousWeight.minWeight))
      }
      formData.value.weights.push(weight)
    }

    const submit = (val: PriceFormData | any) => {
      const price: PriceFormData = {
        ...formData.value,
        ...val,
        destination:
          formData.value.countryCode === 'SE' && formData.value.countryGroup === null ? DOMESTIC : INTERNATIONAL,
        isDefault: false,
      }
      pricesStore.create(price)
      closeModal()
    }

    const reloadPriceData = (value?: BasePrice | null) => {
      if (value?.id !== formData.value.id) {
        formData.value = value?.id ? { ...value } : { ...defaultPriceData }
      }
    }

    const reloadFormData = () => {
      if (formData.value.countryGroup && formData.value.countryCode) {
        formData.value.countryCode = null
      }
      if (formData.value.mode === TIER && formData.value.weights?.length) {
        formData.value.weights = []
      }
      if (formData.value.mode === WEIGHT && !formData.value.weights?.length) {
        formData.value.weights = [{ ...defaultWeightData }]
      }
    }

    watch(modalData, reloadPriceData)
    watch(formData, reloadFormData, { deep: true })

    onMounted(reloadPriceData)

    return {
      modes,
      carriers,
      formData,
      modalData,
      modalName,
      modalTitle,
      countryGroups,
      submit,
      addWeight,
      closeModal,
      selectCountryCode,
      getValidationState,
      WEIGHT,
      TIER,
    }
  },
})
</script>
